import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  UserCheck,
  DollarSign,
  TrendingUp,
  Clock,
  Shield,
  CheckCircle,
  Award,
  BarChart3,
  Target,
  Zap,
  Users,
  Phone,
  Mail,
  Calendar,
  Star,
  Heart,
  Briefcase
} from 'lucide-react';

const RCMIndependentPhysicians = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>RCM for Independent Physicians | Solo Practice | MDeRCM</title>
        <meta 
          name="description" 
          content="Specialized RCM for independent physicians. Maximize collections, minimize denials and reduce admin burden with AI-powered solo practice billing." 
        />
        <meta 
          name="keywords" 
          content="revenue cycle management for independent physicians, solo physician RCM, independent practice billing, solo practitioner medical billing, physician practice management, independent doctor billing services" 
        />
        <meta property="og:title" content="RCM for Independent Physicians | Solo Practice | MDeRCM" />
        <meta property="og:description" content="Specialized RCM for independent physicians. Maximize collections, minimize denials." />
        <link rel="canonical" href="https://mdercm.com/revenue-cycle-management-independent-physicians" />
      </Helmet>

      <main style={{ minHeight: '100vh', backgroundColor: '#ffffff', fontFamily: "'Poppins', sans-serif" }}>
        
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
          color: '#ffffff',
          padding: isMobile ? '60px 20px' : '80px 40px'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ maxWidth: '56rem', margin: '0 auto', textAlign: 'center' }}>
              <div style={{
                display: 'inline-block',
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '8px 20px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                ⭐ Trusted by 500+ Independent Physicians
              </div>
              
              <h1 style={{
                fontSize: isMobile ? '2rem' : '3rem',
                fontWeight: 'bold',
                marginBottom: '24px',
                lineHeight: '1.2'
              }}>
                Revenue Cycle Management for Independent Physicians
              </h1>
              
              <p style={{
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                marginBottom: '32px',
                opacity: 0.95,
                lineHeight: '1.75'
              }}>
                Take back control of your practice finances. Our specialized RCM solutions for solo and independent physicians maximize collections by 20-30%, reduce overhead by 40%, and free up 10-15 hours per week—so you can focus on patient care, not paperwork.
              </p>

              {/* Key Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                gap: '20px',
                marginTop: '40px'
              }}>
                {[
                  { icon: DollarSign, value: '20-30%', label: 'Revenue Increase' },
                  { icon: TrendingUp, value: '40%', label: 'Cost Reduction' },
                  { icon: Clock, value: '10-15hrs', label: 'Time Saved Weekly' },
                  { icon: Award, value: '98%+', label: 'Collection Rate' }
                ].map((stat, idx) => (
                  <div key={idx} style={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <stat.icon size={32} style={{ marginBottom: '12px' }} />
                    <div style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '4px' }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '16px',
                justifyContent: 'center',
                marginTop: '40px'
              }}>
                <Link to="/free-trial" style={{
                  textDecoration: 'none',
                  backgroundColor: '#F26522',
                  color: '#ffffff',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
                >
                  Get Free Practice Assessment →
                </Link>
                
                <a href="tel:+15103566069" style={{
                  textDecoration: 'none',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  border: '2px solid rgba(255,255,255,0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                >
                  📞 Call: +1 (510) 356-6069
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              As an independent physician, you face unique challenges that larger practices don't experience. You're the clinician, administrator, and business owner—all rolled into one. Between seeing patients, managing staff, and handling the business side of your practice, revenue cycle management often gets pushed to the back burner, costing you thousands in lost revenue every month.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              <strong style={{ color: '#0d9488' }}>Revenue cycle management for independent physicians</strong> requires a specialized approach. You don't have the luxury of in-house billing departments, dedicated coders, or full-time RCM staff. You need efficient, cost-effective solutions that deliver big-practice results without big-practice overhead.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151'
            }}>
              Our RCM service is specifically designed for independent physicians and solo practitioners. We become your complete billing department—expert coders, claim specialists, denial managers, and financial analysts—at a fraction of the cost of hiring even one billing employee. The result? More revenue, less stress, and more time for what you do best: practicing medicine.
            </p>
          </div>
        </section>

        {/* Challenges Section */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#f0fdfa' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              7 Critical Challenges Independent Physicians Face
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto'
            }}>
              These issues silently drain your practice's profitability
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  title: '1. Wearing Too Many Hats',
                  problem: 'You\'re simultaneously the doctor, business owner, HR manager, and often the billing supervisor. With 60-70 patient hours per week plus administrative duties, billing management gets only 2-3 hours of rushed attention—not enough to optimize revenue.',
                  impact: 'Result: Missed charges, delayed submissions, unworked denials. Average loss: $3,000-$8,000 monthly.',
                  icon: Briefcase,
                  color: '#0d9488'
                },
                {
                  title: '2. High Cost of In-House Billing',
                  problem: 'Hiring even one billing person costs $45,000-$55,000 annually, plus 30% benefits, software ($10K/year), training, and supervision time. For a practice generating $400K-$600K, this represents 12-18% of revenue.',
                  impact: 'You need $60K-$80K in additional revenue just to break even on billing costs.',
                  icon: DollarSign,
                  color: '#14b8a6'
                },
                {
                  title: '3. Limited Expertise Across Specialties',
                  problem: 'Medical billing spans thousands of codes, constantly changing payer rules, complex modifiers, and specialty-specific requirements. Even experienced billers struggle to maintain expertise across multiple specialties when working solo.',
                  impact: 'Coding errors and missed opportunities cost 5-8% of potential revenue annually.',
                  icon: Target,
                  color: '#0f766e'
                },
                {
                  title: '4. Cash Flow Unpredictability',
                  problem: 'Without systematic processes, claims sit 3-7 days before submission, denials go unworked for weeks, and follow-up happens sporadically. This creates feast-or-famine cash flow that makes budgeting nearly impossible.',
                  impact: '$40K-$80K constantly tied up in delayed payments instead of working capital.',
                  icon: TrendingUp,
                  color: '#0d9488'
                },
                {
                  title: '5. Denial Management Blind Spots',
                  problem: 'Small practices average 12-18% denial rates (vs. 3-5% for specialized billers). Without dedicated denial management, 70% of denied claims never get appealed. Each unworked denial represents $120-$200 in lost revenue.',
                  impact: 'Annual lost revenue from unworked denials: $25,000-$60,000.',
                  icon: Shield,
                  color: '#14b8a6'
                },
                {
                  title: '6. Technology Gaps',
                  problem: 'Professional-grade billing software costs $8K-$15K annually, with additional fees for clearinghouse, eligibility checks, and reporting tools. Many independent physicians use basic systems or manual processes.',
                  impact: 'Inefficient workflows waste 5-8 hours weekly and increase error rates by 15-20%.',
                  icon: Zap,
                  color: '#0f766e'
                },
                {
                  title: '7. No Time for Strategic Revenue Optimization',
                  problem: 'Beyond daily billing operations, optimizing revenue requires analyzing payer contracts, monitoring coding trends, identifying revenue leakage, and implementing improvements. Independent physicians rarely have time for this strategic work.',
                  impact: 'Missed optimization opportunities cost $2,000-$5,000 monthly in preventable revenue loss.',
                  icon: BarChart3,
                  color: '#0d9488'
                }
              ].map((challenge, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  border: `3px solid ${challenge.color}`,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(13, 148, 136, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <challenge.icon size={28} style={{ color: challenge.color }} />
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: challenge.color,
                      margin: 0
                    }}>
                      {challenge.title}
                    </h3>
                  </div>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      THE CHALLENGE:
                    </div>
                    <p style={{
                      fontSize: '0.9375rem',
                      lineHeight: '1.6',
                      color: '#4b5563',
                      margin: 0
                    }}>
                      {challenge.problem}
                    </p>
                  </div>

                  <div style={{
                    backgroundColor: '#f0fdfa',
                    padding: '12px',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${challenge.color}`
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: challenge.color,
                      marginBottom: '6px'
                    }}>
                      💰 FINANCIAL IMPACT:
                    </div>
                    <p style={{
                      fontSize: '0.9375rem',
                      lineHeight: '1.6',
                      color: '#374151',
                      margin: 0
                    }}>
                      {challenge.impact}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Solution */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Complete RCM Solution for Independent Physicians
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto'
            }}>
              Your dedicated billing department—without the overhead
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  icon: UserCheck,
                  title: 'Dedicated Expert Team',
                  items: [
                    'Certified medical coders (CPC/CCS)',
                    'Specialty-trained billing specialists',
                    'Denial management experts',
                    'Personal account manager',
                    'No turnover, no training gaps'
                  ],
                  benefit: 'Save $45K-$65K annually'
                },
                {
                  icon: TrendingUp,
                  title: 'Maximize Revenue',
                  items: [
                    '100% charge capture accuracy',
                    'Optimal coding for every encounter',
                    'Aggressive AR follow-up',
                    '70-80% denial recovery rate',
                    'Proactive denial prevention'
                  ],
                  benefit: 'Increase revenue 20-30%'
                },
                {
                  icon: Clock,
                  title: 'Faster Collections',
                  items: [
                    '24-hour claim submission',
                    'Daily payment posting',
                    'Systematic denial management',
                    'Reduce days in AR by 35-40%',
                    'Predictable cash flow'
                  ],
                  benefit: 'Free up $40K-$80K cash'
                },
                {
                  icon: Shield,
                  title: 'Reduce Denials',
                  items: [
                    'Pre-service eligibility verification',
                    'Authorization tracking & management',
                    'Real-time claim scrubbing',
                    'Coding validation before submission',
                    'Cut denial rates by 40-50%'
                  ],
                  benefit: 'Prevent $20K-$40K losses'
                },
                {
                  icon: BarChart3,
                  title: 'Complete Visibility',
                  items: [
                    'Real-time performance dashboards',
                    'Daily collection reports',
                    'Detailed analytics by payer/CPT',
                    'Denial trend monitoring',
                    'Monthly strategic reviews'
                  ],
                  benefit: 'Data-driven decisions'
                },
                {
                  icon: Zap,
                  title: 'Enterprise Technology',
                  items: [
                    'Advanced billing software included',
                    'EHR integration (all major systems)',
                    'Automated clearinghouse',
                    'Secure patient portal',
                    'No additional tech costs'
                  ],
                  benefit: 'Save $8K-$15K annually'
                }
              ].map((solution, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#f0fdfa',
                  padding: '28px',
                  borderRadius: '12px',
                  border: '2px solid #99f6e4',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#0d9488';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(13, 148, 136, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#99f6e4';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <solution.icon size={40} style={{ color: '#0d9488', marginBottom: '16px' }} />
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>
                    {solution.title}
                  </h3>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 16px 0'
                  }}>
                    {solution.items.map((item, itemIdx) => (
                      <li key={itemIdx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: '10px',
                        fontSize: '0.9375rem',
                        color: '#4b5563'
                      }}>
                        <CheckCircle size={18} style={{ color: '#0d9488', flexShrink: 0, marginTop: '2px' }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div style={{
                    backgroundColor: '#0d9488',
                    color: '#ffffff',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    💰 {solution.benefit}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ROI Calculator */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ecfdf5' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Your Financial Transformation
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#059669',
              marginBottom: '40px',
              fontWeight: '600'
            }}>
              Example: Independent physician collecting $500,000 annually
            </p>

            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '48px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
              marginBottom: '40px'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '32px',
                color: '#0d9488',
                textAlign: 'center'
              }}>
                Annual Financial Impact Analysis
              </h3>

              {/* Cost Savings */}
              <div style={{
                backgroundColor: '#f0fdfa',
                padding: '24px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '2px solid #99f6e4'
              }}>
                <h4 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#0d9488',
                  marginBottom: '16px'
                }}>
                  💰 COST SAVINGS
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { item: 'Billing staff salary & benefits', amount: '$65,000' },
                    { item: 'Billing software & technology', amount: '$12,000' },
                    { item: 'Training & turnover costs', amount: '$8,000' },
                    { item: 'Office space & equipment', amount: '$5,000' },
                    { item: 'Less: RCM service fee (5%)', amount: '($25,000)' }
                  ].map((row, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      fontSize: '0.9375rem'
                    }}>
                      <span style={{ color: '#374151' }}>{row.item}</span>
                      <span style={{
                        fontWeight: '600',
                        color: row.amount.includes('(') ? '#dc2626' : '#059669'
                      }}>{row.amount}</span>
                    </div>
                  ))}
                  <div style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '2px solid #99f6e4',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '1.125rem',
                    fontWeight: '700'
                  }}>
                    <span style={{ color: '#0d9488' }}>Net Cost Savings:</span>
                    <span style={{ color: '#059669' }}>$65,000</span>
                  </div>
                </div>
              </div>

              {/* Revenue Increase */}
              <div style={{
                backgroundColor: '#ecfdf5',
                padding: '24px',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '2px solid #6ee7b7'
              }}>
                <h4 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: '#059669',
                  marginBottom: '16px'
                }}>
                  📈 REVENUE INCREASE
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { item: 'Improved coding accuracy (5%)', amount: '$25,000' },
                    { item: 'Better charge capture (3%)', amount: '$15,000' },
                    { item: 'Reduced denials (4%)', amount: '$20,000' },
                    { item: 'Faster collections (improved cash flow)', amount: '$15,000' },
                    { item: 'Denial recovery on past claims', amount: '$25,000' }
                  ].map((row, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '12px',
                      backgroundColor: '#ffffff',
                      borderRadius: '6px',
                      fontSize: '0.9375rem'
                    }}>
                      <span style={{ color: '#374151' }}>{row.item}</span>
                      <span style={{ fontWeight: '600', color: '#059669' }}>{row.amount}</span>
                    </div>
                  ))}
                  <div style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '2px solid #6ee7b7',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '1.125rem',
                    fontWeight: '700'
                  }}>
                    <span style={{ color: '#059669' }}>Additional Revenue:</span>
                    <span style={{ color: '#059669' }}>$100,000</span>
                  </div>
                </div>
              </div>

              {/* Total Impact */}
              <div style={{
                backgroundColor: '#059669',
                color: '#ffffff',
                padding: '32px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.125rem', marginBottom: '12px', opacity: 0.95 }}>
                  🎉 Total Annual Financial Benefit
                </div>
                <div style={{ fontSize: isMobile ? '2.5rem' : '3.5rem', fontWeight: 'bold', marginBottom: '8px' }}>
                  $165,000
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.9 }}>
                  33% increase in net practice income
                </div>
                <div style={{
                  marginTop: '24px',
                  padding: '16px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}>
                  Plus intangible benefits: 10-15 hours saved weekly, reduced stress, better work-life balance
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Time Savings */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Reclaim Your Time
            </h2>

            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '48px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '32px'
              }}>
                {/* Before */}
                <div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    marginBottom: '24px',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <Clock size={28} />
                    Before: Your Typical Week
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {[
                      { task: 'Reviewing billing reports', hours: '2-3 hrs' },
                      { task: 'Answering billing questions', hours: '2-3 hrs' },
                      { task: 'Handling denial issues', hours: '2-3 hrs' },
                      { task: 'Managing billing staff', hours: '2-3 hrs' },
                      { task: 'Resolving payer problems', hours: '1-2 hrs' },
                      { task: 'Software/tech issues', hours: '1-2 hrs' }
                    ].map((item, idx) => (
                      <div key={idx} style={{
                        padding: '16px',
                        backgroundColor: '#fee2e2',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid #fecaca'
                      }}>
                        <span style={{ fontSize: '0.9375rem', color: '#374151' }}>{item.task}</span>
                        <span style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#dc2626' }}>
                          {item.hours}
                        </span>
                      </div>
                    ))}
                    <div style={{
                      padding: '20px',
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      borderRadius: '8px',
                      textAlign: 'center',
                      marginTop: '8px'
                    }}>
                      <div style={{ fontSize: '0.875rem', marginBottom: '4px', opacity: 0.9 }}>
                        Total Weekly Time
                      </div>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>10-16 hours</div>
                    </div>
                  </div>
                </div>

                {/* After */}
                <div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    marginBottom: '24px',
                    color: '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <Heart size={28} />
                    After: With MDeRCM
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {[
                      { task: 'Review dashboard (10 min)', hours: '30 min' },
                      { task: 'Monthly strategy call', hours: '30 min' },
                      { task: 'Answer occasional questions', hours: '30 min' },
                      { task: 'Total management time', hours: '90 min' }
                    ].map((item, idx) => (
                      <div key={idx} style={{
                        padding: '16px',
                        backgroundColor: idx === 3 ? '#ecfdf5' : '#f0fdfa',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: idx === 3 ? '2px solid #6ee7b7' : '1px solid #99f6e4',
                        fontWeight: idx === 3 ? '600' : '400'
                      }}>
                        <span style={{ fontSize: '0.9375rem', color: '#374151' }}>{item.task}</span>
                        <span style={{ fontSize: '0.9375rem', fontWeight: '600', color: '#059669' }}>
                          {item.hours}
                        </span>
                      </div>
                    ))}
                    <div style={{
                      padding: '20px',
                      backgroundColor: '#059669',
                      color: '#ffffff',
                      borderRadius: '8px',
                      textAlign: 'center',
                      marginTop: '8px'
                    }}>
                      <div style={{ fontSize: '0.875rem', marginBottom: '4px', opacity: 0.9 }}>
                        Total Weekly Time
                      </div>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>1.5 hours</div>
                    </div>

                    <div style={{
                      marginTop: '16px',
                      padding: '20px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '8px',
                      border: '2px solid #fbbf24'
                    }}>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#92400e',
                        marginBottom: '8px'
                      }}>
                        ⏰ Time Saved Per Week:
                      </div>
                      <div style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#92400e'
                      }}>
                        10-15 hours
                      </div>
                      <div style={{
                        fontSize: '0.875rem',
                        color: '#92400e',
                        marginTop: '8px'
                      }}>
                        Use for: 6-8 more patients, family time, or personal health
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Implementation Process */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#f0fdfa' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Smooth 30-Day Transition
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {[
                {
                  week: 'Week 1',
                  title: 'Discovery & Setup',
                  tasks: [
                    'Practice assessment & EHR integration',
                    'Account configuration & access setup',
                    'Staff introduction & training coordination',
                    'Baseline metrics establishment'
                  ]
                },
                {
                  week: 'Week 2',
                  title: 'Knowledge Transfer',
                  tasks: [
                    'Review current processes & pain points',
                    'Document specialty-specific requirements',
                    'Transition active AR & open claims',
                    'Quality assurance protocols setup'
                  ]
                },
                {
                  week: 'Week 3',
                  title: 'Parallel Processing',
                  tasks: [
                    'Process claims alongside existing process',
                    'Verify accuracy & completeness',
                    'Fine-tune workflows & procedures',
                    'Address any integration issues'
                  ]
                },
                {
                  week: 'Week 4+',
                  title: 'Full Transition & Optimization',
                  tasks: [
                    'Complete takeover of all billing functions',
                    'Daily monitoring & reporting begins',
                    'Continuous process improvement',
                    'Regular strategy sessions'
                  ]
                }
              ].map((phase, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '24px',
                  border: '2px solid #99f6e4',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: '#0d9488',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      {idx + 1}
                    </div>
                    <div>
                      <div style={{
                        fontSize: '0.875rem',
                        color: '#0d9488',
                        fontWeight: '600',
                        marginBottom: '4px'
                      }}>
                        {phase.week}
                      </div>
                      <h3 style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: '#111827',
                        margin: 0
                      }}>
                        {phase.title}
                      </h3>
                    </div>
                  </div>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {phase.tasks.map((task, taskIdx) => (
                      <li key={taskIdx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        fontSize: '0.9375rem',
                        color: '#4b5563'
                      }}>
                        <CheckCircle size={18} style={{ color: '#0d9488', flexShrink: 0, marginTop: '2px' }} />
                        <span>{task}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Common Questions from Independent Physicians
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                {
                  q: 'How is this different from hiring a billing person?',
                  a: 'You get an entire team (coders, billers, denial specialists, account manager) for less than the cost of one employee. No benefits, no turnover, no training time. Plus enterprise-level technology and expertise across multiple specialties—something a single billing person can\'t provide.'
                },
                {
                  q: 'Will I lose control of my billing?',
                  a: 'No. You maintain complete oversight with real-time dashboards, daily reports, and monthly strategy sessions. We handle the technical work; you stay informed and make all major decisions. Think of us as your dedicated billing department that reports to you.'
                },
                {
                  q: 'What if I already have a billing person?',
                  a: 'Many independent physicians transition their billing person to front-desk or clinical support roles where they add more value. Or, we can work alongside your staff initially. We\'ve helped hundreds of practices make smooth transitions that benefit everyone.'
                },
                {
                  q: 'How quickly will I see ROI?',
                  a: 'Most independent physicians see positive cash flow impact within 30-60 days. Cost savings are immediate (no more salary, benefits, software fees). Revenue improvements typically show within 90 days as clean claims process faster and denials are recovered. Full ROI usually achieved within 4-6 months.'
                },
                {
                  q: 'Do you work with my EHR system?',
                  a: 'Yes. We integrate with all major EHR/PM systems including Epic, Cerner, eClinicalWorks, Athenahealth, Kareo, AdvancedMD, Practice Fusion, DrChrono, and dozens more. Integration is secure, seamless, and doesn\'t disrupt your clinical workflow.'
                },
                {
                  q: 'What if I want to go back to in-house billing?',
                  a: 'We offer flexible contracts with no long-term commitment required. Many physicians choose month-to-month arrangements. If you ever want to bring billing back in-house, we provide complete transition support and documentation. However, 98% of our independent physician clients stay with us because the results speak for themselves.'
                }
              ].map((faq, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#f9fafb',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#0d9488',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <Star size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{faq.q}</span>
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    lineHeight: '1.75',
                    color: '#374151',
                    margin: 0,
                    paddingLeft: '28px'
                  }}>
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
          color: '#ffffff'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              Ready to Increase Revenue by $100,000+ While Saving 10-15 Hours Weekly?
            </h2>
            <p style={{
              fontSize: '1.125rem',
              marginBottom: '32px',
              opacity: 0.95
            }}>
              Join 500+ independent physicians who transformed their practice finances
            </p>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '16px',
              justifyContent: 'center',
              marginBottom: '32px'
            }}>
              <Link to="/free-trial" style={{
                textDecoration: 'none',
                backgroundColor: '#F26522',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block'
              }}>
                Get Free Practice Assessment →
              </Link>
              
              <a href="tel:+15103566069" style={{
                textDecoration: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block',
                border: '2px solid rgba(255,255,255,0.3)'
              }}>
                📞 +1 (510) 356-6069
              </a>
            </div>

            <p style={{
              fontSize: '0.9375rem',
              opacity: 0.9
            }}>
              ⭐ First 30 days FREE • No setup fees • Month-to-month options available
            </p>
          </div>
        </section>

        {/* Related Services */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              textAlign: 'center',
              marginBottom: '32px',
              color: '#111827'
            }}>
              Related Solutions for Physicians
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px'
            }}>
              {[
                { title: 'Small Practice Billing', link: '/medical-billing-outsourcing-small-practices' },
                { title: 'Reduce Claim Denials', link: '/reduce-claim-denials-healthcare-billing' },
                { title: 'View All Pricing', link: '/pricing' }
              ].map((link, idx) => (
                <Link
                  key={idx}
                  to={link.link}
                  style={{
                    padding: '20px',
                    backgroundColor: '#f0fdfa',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    border: '2px solid #99f6e4',
                    transition: 'all 0.3s ease',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#0d9488';
                    e.currentTarget.style.backgroundColor = '#ccfbf1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#99f6e4';
                    e.currentTarget.style.backgroundColor = '#f0fdfa';
                  }}
                >
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#0d9488'
                  }}>
                    {link.title} →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default RCMIndependentPhysicians;